import {
  ReducerBuilder,
  reducerWithInitialState,
} from 'typescript-fsa-reducers';
import * as qs from 'querystring';
import { AnyAction } from 'redux';

import * as api from '../api';
import * as store from './store';

import * as Encounter from './encounter';
import * as Form from './form';
import * as FormField from './formField';
import * as FormSet from './formSet';
import * as Inbound from './inbound';
import * as Login from './login';
import * as Patient from './patient';
import * as Settings from './settings';

import { isEnumMember } from 'common/dist/utils/types';

function getMaybePreferredLanguageFormLocalStorage(): store.Language {
  const preferredLanguage = localStorage.getItem('preferredLanguage');
  if (isEnumMember(api.Language.Language, preferredLanguage)) {
    return preferredLanguage;
  }
  const queryString = window.location.href.split('?');
  const { language } = qs.decode(queryString[1] || '');
  return language && isEnumMember(api.Language.Language, language)
    ? language
    : api.Language.Language.En;
}

const getInitialState = (): store.All => ({
  formSets: {},
  loggedInPatient: null,
  masterPracticePreference: {
    alcoholStatuses: [],
    allergyExplicitNoneEnabled: false,
    allergyNames: [],
    allergySeverityOptions: [],
    allowSelfForGuardianRelationship: false,
    allowedAppointmentNotificationModalities: [],
    collectEmployerPhoneNumber: true,
    collectFeedback: false,
    collectSubscriberInfo: false,
    contraceptiveOptions: [],
    copayPrepayDisplaySetting:
      api.PracticePreference.CopayPrepayDisplaySetting.CopayIfNoPrepay,
    digitalArrivalNotificationSendTimeOffset: null,
    emergencyContactRelationships: [],
    employers: [],
    employmentStatuses: [],
    enableOutreachIntakes: false,
    enablePaymentReceipts: false,
    enablePrevisitPassbookAndPayPass: true,
    enablePrevisitSubscriberToGuarantorRelationshipCollection: true,
    enableProviderDirectory: false,
    exportInsuranceWithNullCoverageStartDateEnabled: false,
    familyMedicalHistoryRelationships: [],
    frontDeskCheckInEnabled: false,
    hideEncounterStartTimeByVisitKind: [],
    introLabel: '',
    lastLabel: '',
    medicalConditionsOptions: [],
    patientAlcoholUsageFrequencies: [],
    patientAlcoholUsageQuantities: [],
    patientAppRteEnabled: false,
    patientEnvironmentLivesWithOptions: [],
    patientEnvironmentLivingSituationOptions: [],
    patientEnvironmentParentsMaritalStatusOptions: [],
    patientEnvironmentRisksOptions: [],
    patientGuarantorDetailsEnabled: true,
    patientInsuranceInformationPayors: [],
    patientRelationshipsToGuarantor: {
      isReverseRelationship: false,
      relationships: [],
    },
    patientRelationshipsToSubscriber: {
      isReverseRelationship: false,
      relationships: [],
    },
    patientSubstanceUsageFrequencies: [],
    previousBalanceCollectionSetting:
      api.PracticePreference.PreviousBalanceCollectionSetting.Off,
    previsitAddressValidCharacterSet:
      api.PracticePreference.PrevisitAddressValidCharacterSet.Any,
    previsitCollectSubscriberRetirementDateEnabled: false,
    previsitConfirmCancelFormSetRedirectEnabled: false,
    previsitConfirmCancelMinimumCancelTime: null,
    previsitDefaultCheckRequiredDocuments: false,
    previsitEthnicityOptions: [],
    previsitMaritalStatusOptions: [],
    previsitMedicationComplianceOptions: [],
    previsitMergeSubscriberGuarantorsEnabled: true,
    previsitMultipleLanguages: false,
    previsitMultipleRaces: false,
    previsitPayorDisplayMapping: {},
    previsitPreferredLanguageOptions: [],
    previsitPreferredPronounOptions: [],
    previsitProviderNameDisplays: [],
    previsitSexOptions: [],
    previsitSkipIntroSingleForm: false,
    previsitUseNotionalSchedulingTimeSlots: false,
    previsitWrittenLanguageOptions: [],
    raceOptions: [],
    religionOptions: [],
    scheduleAppointmentRedirectUrl: '',
    showArrivalTimeOnly: false,
    skipSendingAppointmentConfirmationOnBooking: false,
    stripeTestModeEnabled: false,
    subscriberRelationshipsToGuarantor: {
      isReverseRelationship: false,
      relationships: [],
    },
    substanceNameOptions: [],
    substanceUsageStatuses: [],
    tobaccoStatuses: [],
    tobaccoTreatments: [],
    unsupportedCardBrands: [],
  },
  selectedFormId: null,
  selectedFormSetId: null,
  patientFeedback: null,
  isHeaderHidden: false,
  authenticationCode: null,
  insuranceCards: {
    data: [],
    loading: true,
    error: null,
    updated: null,
  },
  theme: null,
  documentTitle: null,
  preferredLanguage: getMaybePreferredLanguageFormLocalStorage(),
  multiFormSet: false,
  coordinates: undefined,
  onCaptchaSuccess: null,
  inbound: {
    bookedSlot: null,
    selectedProviderId: null,
    ehrProviderInformation: null,
    selectedEhrTimeSlot: null,
    provisionalPatientId: null,
    urlIdentifier: null,
    organizationId: '',
    organizationSlug: null,
    isProvisionalPatientReconciled: false,
  },
  embedded: null,
});

export const resetEnhancer =
  (rootReducer: ReducerBuilder<store.All, store.All, store.All | undefined>) =>
  (state: store.All | undefined, action: AnyAction) => {
    if (action.type !== 'RESET') return rootReducer(state, action);

    const initialState = getInitialState();
    const newState = rootReducer(initialState, action);
    return newState;
  };

export const rootReducer = reducerWithInitialState(getInitialState())
  .case(Encounter.setDocuments, Encounter.setDocumentsHandler)
  .case(Form.setSelectedFormId, Form.setSelectedFormIdHandler)
  .case(Form.setFormStatus, Form.setFormStatusHandler)
  .case(FormField.setFormField, FormField.setFormFieldHandler)
  .case(FormSet.setPatientFeedback, FormSet.setPatientFeedbackHandler)
  .case(Login.setLoggedInPatient, Login.setLoggedInPatientHandler)
  .case(Patient.setAuthenticationCode, Patient.setAuthenticationCodeHandler)
  .case(Settings.setHeaderHidden, Settings.setHeaderHiddenHandler)
  .case(Settings.setTheme, Settings.setThemeHandler)
  .case(Settings.setDocumentTitle, Settings.setDocumentTitleHandler)
  .case(Settings.setPreferredLanguage, Settings.setPreferredLanguageHandler)
  .case(Settings.setOnCaptchaSuccess, Settings.setOnCaptchaSuccessHandler)
  .case(Inbound.setBookedSlot, Inbound.setBookedSlotHandler)
  .case(Inbound.setSelectedProviderId, Inbound.setSelectedProviderIdHandler)
  .case(
    Inbound.setEhrProviderInformationList,
    Inbound.setEhrProviderInformationListHandler,
  )
  .case(Inbound.setSelectedEhrTimeSlot, Inbound.setSelectedEhrTimeSlotHandler)
  .case(Inbound.setProvisionalPatientId, Inbound.setProvisionalPatientIdHandler)
  .case(Inbound.setUrlIdentifier, Inbound.setUrlIdentifierHandler)
  .case(Inbound.setOrganizationId, Inbound.setOrganizationIdHandler)
  .case(Inbound.setOrganizationSlug, Inbound.setOrganizationSlugHandler)
  .case(
    Inbound.setIsProvisionalPatientReconciled,
    Inbound.setIsProvisionalPatientReconciledHandler,
  )
  .casesWithAction(
    [
      Patient.insuranceCardActionCreators.started,
      Patient.insuranceCardActionCreators.done,
      Patient.insuranceCardActionCreators.failed,
    ],
    Patient.fetchInsuranceCardsHandler,
  )
  .case(FormSet.setSelectedFormSetId, FormSet.setSelectedFormSetIdHandler)
  .case(FormSet.setMultiFormSet, FormSet.setMultiFormSetHandler)
  .case(FormSet.setFormSets, FormSet.setFormSetsHandler)
  .case(Patient.setCoordinates, Patient.setCoordinatesHandler)
  .case(Patient.setEmbedded, Patient.setEmbeddedHandler);

export { setDocuments } from './encounter';
export { setFormStatus, setSelectedFormId } from './form';
export { setFormField } from './formField';
export {
  setPatientFeedback,
  setSelectedFormSetId,
  setMultiFormSet,
  setFormSets,
} from './formSet';
export { setLoggedInPatient } from './login';
export { setAuthenticationCode, setCoordinates, setEmbedded } from './patient';
export {
  setHeaderHidden,
  setTheme,
  setDocumentTitle,
  setPreferredLanguage,
  setOnCaptchaSuccess,
} from './settings';
export {
  setBookedSlot,
  setEhrProviderInformationList,
  setIsProvisionalPatientReconciled,
  setOrganizationId,
  setOrganizationSlug,
  setProvisionalPatientId,
  setSelectedEhrTimeSlot,
  setSelectedProviderId,
  setUrlIdentifier,
} from './inbound';
